import {
  createTheme,
  responsiveFontSizes,
  ThemeOptions,
} from '@material-ui/core'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import BackgroundImage from 'images/bg_metal_tekening.png'
import _ from 'lodash'
import Logo from '_icons/logo/QF_logoVrt.png'

declare module '@material-ui/core/styles/createTheme' {
  interface VerticalMenu {
    backgroundColor?: string
    color?: string
    boxShadow?: string
  }

  interface IconColors {
    outline?: string
    highlight?: string
    white?: string
  }

  interface Theme {
    logoSrc?: string
    verticalMenu?: VerticalMenu
    BoMThumbnail: {
      mixBlendMode: CSSProperties['mixBlendMode']
    }
    googleFonts?: string
    backgroundImageSrc?: string
    iconColors?: IconColors
  }

  interface ThemeOptions {
    logoSrc?: string
    verticalMenu?: VerticalMenu
    BoMThumbnail?: {
      mixBlendMode: CSSProperties['mixBlendMode']
    }
    googleFonts?: string
    backgroundImageSrc?: string
    iconColors?: IconColors
  }
}

const defaultOverrides: ThemeOptions = {
  verticalMenu: {
    boxShadow: 'rgba(0, 0, 0, 0.3) 4px 4px 10px 0px',
  },
  // palette: {
  //   contrastThreshold: 3,
  // },
  iconColors: {
    outline: '#80878F',
    highlight: '#05B5CE',
    white: '#FFFFFF',
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        a: {
          color: 'inherit',
        },
      },
    },
  },
  typography: {
    fontSize: 12,
    button: {
      textTransform: 'none',
    },
  },
  props: {
    MuiTooltip: {
      arrow: true,
    },
    MuiBadge: {
      overlap: 'rectangular',
    },
  },
}

const createAppTheme = (rh24ThemeOptions: ThemeOptions) => {
  return responsiveFontSizes(
    createTheme(_.merge({}, defaultOverrides, rh24ThemeOptions))
  )
}

const darkBlueTheme: ThemeOptions = {
  logoSrc: `${window.location.origin}${Logo}`,
  BoMThumbnail: {
    mixBlendMode: 'normal',
  },
  googleFonts: 'Montserrat',
  typography: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 12,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: '#24344d',
        boxShadow: '0px 0 2px 0 #a8a7a6',
      },
    },
    MuiFormControlLabel: {
      root: {
        whiteSpace: 'nowrap' as never,
      },
    },
    MuiFormControl: {
      root: {
        whiteSpace: 'nowrap',
      },
    },
  },

  verticalMenu: {
    backgroundColor: '#24344d',
    boxShadow: '0px 0 2px 0 #a8a7a6',
  },

  iconColors: {
    white: 'transparent',
    highlight: '#05B5CE',
    outline: '#FFF',
  },

  palette: {
    type: 'dark',
    primary: {
      main: '#3e7bfa',
    },
    secondary: {
      main: '#E63F4D',
    },
    background: {
      default: '#32334D',
      paper: '#27283b',
    },
  },
  props: {
    MuiInputLabel: {
      shrink: true,
    },
  },
}

const lightDefaultTheme: ThemeOptions = {
  BoMThumbnail: {
    mixBlendMode: 'multiply',
  },
  googleFonts: 'Montserrat',
  typography: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 12,
  },
  verticalMenu: {
    // backgroundColor: colors.grey[200],
    backgroundColor: '#fafafa',
    boxShadow: '0px 0 2px 0 #a8a7a6',
  },
  backgroundImageSrc: `${window.location.origin}${BackgroundImage}`,
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          backgroundColor: '#fafafa',
        },
      },
    },
    MuiCard: {
      root: {
        boxShadow: '0px 0 2px 0 #a8a7a6',
        borderRadius: 0,
        overflow: 'inherit',
      },
    },
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: '#fafafa',
        color: '#0a0a0a',
      },
      root: {
        boxShadow: '0px 0 2px 0 #a8a7a6',
      },
    },
    MuiFormControlLabel: {
      root: {
        whiteSpace: 'nowrap' as never,
      },
    },
    MuiFormControl: {
      root: {
        whiteSpace: 'nowrap' as never,
      },
    },
  },
  iconColors: {
    outline: '#80878F',
    highlight: '#05B5CE',
    white: '#FFFFFF',
  },
  palette: {
    // primary: { main: colors.blue['700'] },
    primary: { main: '#5B5AC5' },
    secondary: { main: '#E63F4D' },
    text: {
      primary: '#444443',
      secondary: 'rgba(52, 52, 52, 0.7)',
    },
    background: {
      // default: colors.grey['300'],
      default: '#fafafa',
      paper: '#fff',
    },
  },
  props: {
    MuiInputLabel: {
      shrink: true,
    },
  },
  logoSrc: `${window.location.origin}${Logo}`,
}

export type BuiltInThemes = 'light' | 'darkBlue'

export { createAppTheme, lightDefaultTheme as rh24WebAppTheme, darkBlueTheme }
